import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/Header";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { makeAuthenticatedGet } from "../../utils/fetchUtils";
import { GET_ACCOUNT_INFO, GET_USER_INFO } from "../../utils/constants";
import { IAccount, IUser } from "../../utils/types";
import SideBar from "../../components/Sidebar";
import useWindowWidth from "../../utils/windowWidth";
import user from "../../assets/user.svg";
import linkedIn from "../../assets/linkedIn.svg";
import twitter from "../../assets/twitter.svg";
import link from "../../assets/link-45deg.svg";
import edit from "../../assets/edit.svg";

const Profile = () => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<IUser | null>(null);
  const [accountDetails, setAccountDetails] = useState<IAccount | null>(null);

  const socialImg: any[] = [linkedIn, twitter, link];

  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const profileUpdateMessage = (location.state as { profileUpdateMessage?: string } | null)?.profileUpdateMessage;

  useEffect(() => {
    if (
      localStorage.getItem("token") === null ||
      localStorage.getItem("token") === undefined
    ) {
      navigate("/login");
    }
    document.title = "Profile - Nclude Portal";
    fetchUserDetails();
    fetchAccountInfo();
  }, [navigate]);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  const fetchUserDetails = async () => {
    try {
      const response = await makeAuthenticatedGet(GET_USER_INFO);
      if (response.success) setUserDetails(response.user);
    } catch (error) {}
  };

  const fetchAccountInfo = async () => {
    try {
      const response = await makeAuthenticatedGet(GET_ACCOUNT_INFO);
      if (response.success !== false) {
        setAccountDetails(response.accountInfo ?? response.data);
      }
    } catch (error) {}
  };

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  function userEdit(): void {
    navigate('/edit-profile');
  }

  function accountEdit(): void {
    navigate('/edit-account');
  }

  return (
    <div className="">
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Profile" />
        <main
          className="doc-container px-0 py-0"
          id="main-content"
          tabIndex={-1}
          style={{ position: "relative" }}
        >
          <div className="px-10 pb-12 pt-12 profile-header">
            <h1 className="display-6" ref={mainRef} tabIndex={-1}>
              {"Profile"}
            </h1>
            {profileUpdateMessage && (
              <p className="profile-update-confirmation" role="status" aria-live="polite">
                {profileUpdateMessage}
              </p>
            )}
          </div>
          <section className="user-header" aria-labelledby="profile-summary-heading">
            <img src={user} alt="" aria-hidden="true" />
            <div>
              <h2 className="text-2xl" id="profile-summary-heading">{userDetails?.userName || "Profile summary"}</h2>
              <p
                className="text-l"
                style={{ color: "rgba(120, 116, 134, 1)", margin: 0 }}
              >
                {userDetails?.email}
              </p>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <p
                className="text-l"
                style={{ color: "rgba(120, 116, 134, 1)", margin: 0 }}
              >
                {userDetails?.phoneNumber}
              </p>
            </div>
          </section>
          <div className="user-profile-container">
            <section className="left" aria-labelledby="profile-information-heading">
              <button type="button" className="edit-icon-button" onClick={userEdit} aria-label="Edit profile information">
                <img src={edit} alt="" aria-hidden="true" />
              </button>
              <h2 id="profile-information-heading" className="profile-section-heading">Profile Information</h2>
              <p>{userDetails?.profileInfo}</p>
              <p>
                <span>Full Name: </span>
                {userDetails?.userName}
              </p>
              <p>
                <span>Mobile: </span>
                {userDetails?.phoneNumber}
              </p>
              <p>
                <span>Email: </span>
                {userDetails?.email}
              </p>
              <p>
                <span>Resume: </span>
                {userDetails?.resume ? (
                  <a
                    className="profile-resume-download"
                    href={userDetails.resume}
                    download
                  >
                    Download resume
                  </a>
                ) : "No resume uploaded"}
              </p>
              <div className="d-flex profile-social-links">
                <span>Social Media: </span>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "fit-content",
                    marginLeft: "1rem",
                  }}
                >
                  {userDetails?.social?.map((url, index) => {
                    return (
                      <a
                        href={url}
                        key={url || index}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open social profile ${index + 1} in a new tab`}
                      >
                        <img
                          src={socialImg[index] || link}
                          alt=""
                          aria-hidden="true"
                          style={{ height: "0.875rem", width: "0.875rem" }}
                        />
                      </a>
                    );
                  })}
                </div>
              </div>
            </section>
            <section className="right" aria-labelledby="account-information-heading">
              <button type="button" className="edit-icon-button" onClick={accountEdit} aria-label="Edit account information">
                <img src={edit} alt="" aria-hidden="true" />
              </button>
              <h2 id="account-information-heading" className="profile-section-heading">Account Information</h2>
              {/* <p>
                Hi, I’m Alec Thompson, Decisions: If you can’t decide, the
                answer is no. If two equally difficult paths, choose the one
                more painful in the short term (pain avoidance is creating an
                illusion of equality).
              </p> */}
              <p>
                <span>Employment Status: </span>
                {accountDetails?.employmentStatus}
              </p>
              <p>
                <span>Job Search Status: </span>
                {accountDetails?.jobSearchStatus}
              </p>
              <p>
                <span>Disability: </span>
                {accountDetails?.disability}
              </p>
              <p>
                <span>Preferred Industry: </span>
                {accountDetails?.preferredIndustry}
              </p>
              <p>
                <span>Preferred Role: </span>
                {accountDetails?.preferredRole}
              </p>
              <div className="bottom-border"></div>
              <Link className="anchor" to='/profile/reset-password'>Reset Password</Link>
              {!userDetails?.email && <Link className="anchor" to='/link/email'>
                Link Email
              </Link>}
              {!userDetails?.phoneNumber && <Link className="anchor" to='/link/phone'>
                Link phone number
              </Link>}
            </section>
            {/* <div className="flex profile-name">
              <h2 className="text-3xl">{userDetails?.userName}</h2>
            </div>
            <div className="profile-details">
              <div className="flex ">
                <p className="text-xl px-4">Email: </p>
                <p className="text-xl">{userDetails?.email}</p>
              </div>
              <div className="flex ">
                <p className="text-xl px-4">Role: </p>
                <p className="text-xl">
                  {userDetails?.isOrg ? "Orgid" : "Individual"}
                </p>
              </div>
              <div className="flex ">
                <p className="text-xl px-4">Phone: </p>
                <p className="text-xl">{userDetails?.phoneNumber}</p>
              </div>
            </div> */}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
